import React, { useEffect, useState, useRef } from 'react';
import ResetPasswordLayout from './layout_01/ResetPasswordLayout';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '../../common/hooks';
import { useAppSelector } from '../../store/hook';
import { setCookie } from '../../common/utils/cookies';
import axios from '../../common/utils/interceptor';
import { apiUrls } from '../../constants';
import { getLocalizedKeyByErrorMessage } from '../../authentication/utils/errorMessage';
import PaperContainer from './PaperContainer';
import { FullPageLoader } from '../../common/components/FullPageLoader';
import { fetchTemplateSettings } from '../../common/utils/fetchTemplateSettings';
import { fetchExperienceProps } from '../../common/utils/fetchExperienceProps';
import { getFormattedLocale } from '../utils/getFromattedLocale';
import { candidateProvider } from '../../store/providers/candidateProvider';
import { basicDetailsProvider } from '../../store/providers/basicDetailsProvider';
import { updateSessionStorageForLoggedinUser } from '../../authentication/utils/updateSessionStorage';
import { initTCPlus } from './../../base/utils/initTCPlus';
import { reValidateSession } from '../../authentication/utils/revalidateSession';
import { redirectUrlOnAuthentication } from '../../common/utils/redirectUrlOnAuthentication';
import { handleRegisterAssessmentForTCPlus } from '../utils/loginSuccessHandler';
import { tcPlusCompanySettings } from '../../authentication/utils/tcPlusCompanySettings';
import { a11yFocusApiErrors } from '../../common/utils/accessibilityGenericMessage';

const ResetPasswordPage = (): React.JSX.Element => {
  const navigate = useNavigate();
  const errorMessageRestPasswordRef = useRef<HTMLDivElement>(null);
  const { setCandidate } = candidateProvider();
  const { updateUserSessionActive } = basicDetailsProvider();
  const query = useQuery();
  const workflowSession = useAppSelector((state) => state.workflowSession);
  const token = query.get('token');
  const wfSessionId = query.get('workflowSessionId');
  const template = useAppSelector((state) => state.template);
  const candidate = useAppSelector((state) => state.candidate);

  if (token) {
    setCookie('TcPlusFlag', '1');
  }

  /** states for Reset Password fields */
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [passwordHelperText, setPasswordHelperText] = useState<string>('');

  /** state for info/error alert */
  const [alert, setAlert] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingTemplateSettings, setLoadingTemplateSettings] = useState<boolean>(!template.id);

  /** Get template & company settings */
  useEffect(() => {
    (async (): Promise<void> => {
      if (!template.id) {
        const requestData = { workflowSessionId: wfSessionId };
        const locale = query.get('locale');
        const formattedLocale = getFormattedLocale(locale);
        setCookie('locale_player', formattedLocale!);
        await fetchTemplateSettings(requestData);
        await fetchExperienceProps();
        await tcPlusCompanySettings(true);
        setLoadingTemplateSettings(false);
      }
    })();
  }, [template.id, wfSessionId]);

  /** api to process change password */
  const changePassword = async (): Promise<void> => {
    setLoading(true);
    try {
      const request = {
        wfSessionId: wfSessionId,
        token: token,
        newpassword: password,
      };
      const response = await axios.post(apiUrls.CHANGE_PASSWORD_TCPLUS, request);
      if (response.data.IsError) {
        a11yFocusApiErrors(errorMessageRestPasswordRef);
        setAlert(getLocalizedKeyByErrorMessage(response.data.Message));
      }
      if (response.data['Success']) {
        setCandidate(response.data.Data.candidate);
        updateUserSessionActive(true);
        updateSessionStorageForLoggedinUser();
        const testLinkURL = `${window.location.origin}/experience/#/link/${response.data['link']}`;
        await initTCPlus({ link: response.data['link'], resetIndicator: true, testLinkURL: testLinkURL }, navigate);
        const reValidateSessionObj = await reValidateSession();
        const data = await handleRegisterAssessmentForTCPlus(response.data.Data.candidate, workflowSession, true);
        if (data.url) {
          navigate(data.url);
          return;
        }
        if (data.error) {
          setAlert(data.error);
          return;
        }
        if (reValidateSessionObj.data && !reValidateSessionObj.error) {
          const url = await redirectUrlOnAuthentication(reValidateSessionObj.data, { template, candidate });
          if (url) {
            navigate(url);
          }
        }
      }
    } catch (e) {
      if (e && e.data && e.data.Message) {
        a11yFocusApiErrors(errorMessageRestPasswordRef);
        setAlert(getLocalizedKeyByErrorMessage(e.data.Message));
      }
      setAlert(getLocalizedKeyByErrorMessage('genericError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <FullPageLoader isLoading={loadingTemplateSettings} />
      {!loadingTemplateSettings && (
        <PaperContainer variant="small">
          <ResetPasswordLayout
            password={password}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            loading={loading}
            setPassword={setPassword}
            passwordHelperText={passwordHelperText}
            setPasswordHelperText={setPasswordHelperText}
            changePassword={changePassword}
            alert={alert}
            errorMessageRestPasswordRef={errorMessageRestPasswordRef}
          />
        </PaperContainer>
      )}
    </>
  );
};
export default ResetPasswordPage;
